import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { AccountCreditCardModal } from '@retainerclub/common';
import {
  CREATE_SUBSCRIPTION_ACKNOWLEDGMENT,
  GET_PENDING_SUBSCRIPTION_ACKNOWLEDGMENTS,
} from '@retainerclub/customer-api';
import { useAccount, useAuth } from '@retainerclub/hooks';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { needsConsent } from '../AccountConsent/helpers/needsConsent';
import { productNoun } from './helpers/disclosureCopy';
import SubscriptionDisclosureModal from './SubscriptionDisclosureModal';

// The gate cannot be backed out of; the modal is rendered non-dismissable.
function noop() {
  return undefined;
}

// Routes where the customer is not yet (or no longer) an account holder in
// the app, so no interstitial should appear.
const EXEMPT_PATH_PREFIXES = [
  '/onboarding',
  '/signin',
  '/forgot-password',
  '/reset-password',
  '/new-password',
];

// Dashboard interstitial: when a partner adds a plan or subscription to an
// account whose holder has already onboarded, neither checkout nor onboarding
// can capture the disclosure. This shows the same modal on every page load
// until each pending item is acknowledged. Production of those orders is held
// server-side until then.
function SubscriptionAcknowledgmentGate() {
  const { currentUser } = useAuth();
  const { account, accountRefetch } = useAccount();
  const { pathname } = useLocation();
  const [errorMessages, setErrorMessages] = useState(null);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [acknowledgedCount, setAcknowledgedCount] = useState(0);

  const exempt = EXEMPT_PATH_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );

  const { data, refetch } = useQuery(GET_PENDING_SUBSCRIPTION_ACKNOWLEDGMENTS, {
    fetchPolicy: 'network-only',
    skip: !currentUser || exempt,
  });

  // The layout stays mounted across client-side navigation, so the query
  // above runs once per page load. A partner can add a plan while the
  // customer is signed in; re-checking on every route change surfaces it on
  // their next click instead of their next reload. The first render is
  // skipped because the query itself already fetches on mount.
  const lastCheckedPath = useRef(pathname);
  useEffect(() => {
    if (lastCheckedPath.current === pathname) {
      return;
    }
    lastCheckedPath.current = pathname;
    if (currentUser && !exempt) {
      refetch();
    }
  }, [pathname, currentUser, exempt, refetch]);
  const [createSubscriptionAcknowledgment, { loading }] = useMutation(
    CREATE_SUBSCRIPTION_ACKNOWLEDGMENT,
  );
  const client = useApolloClient();

  const items = data?.pendingSubscriptionAcknowledgments?.items || [];
  const item = items[0] || null;
  const tiers = data?.pendingSubscriptionAcknowledgments?.tiers || null;
  const requiresCard =
    data?.pendingSubscriptionAcknowledgments?.requiresPaymentMethod === true;
  const hasCard = account?.validCreditCard === true;

  // The interstitial closes while the card modal is open: two Headless UI
  // dialogs open at once leave the first one unable to receive clicks after
  // the second closes. Its typed name, agreement and tier choice live in
  // SubscriptionDisclosureModal state, so they survive the round trip.
  async function handleCardModalClose() {
    setIsCardModalOpen(false);
    await Promise.all([accountRefetch?.(), refetch()]);
  }

  async function handleConfirm(submission) {
    setErrorMessages(null);
    const { itemId, ...rest } = submission;

    try {
      const { data: result } = await createSubscriptionAcknowledgment({
        variables: {
          input: {
            input: { orderItemId: itemId, ...rest },
            clientMutationId: crypto.randomUUID(),
          },
        },
      });

      const payload = result?.createSubscriptionAcknowledgment;
      const errors = payload?.errorMessages || [];
      if (errors.length > 0 || !payload?.accountEvent?.id) {
        setErrorMessages(
          errors.length > 0
            ? errors
            : [
                {
                  path: 'base',
                  message:
                    'We could not save your acknowledgment. Please try again.',
                },
              ],
        );
        return;
      }

      setAcknowledgedCount((count) => count + 1);
      await refetch();
      // The acknowledgment may retarget a pending subscription, so refresh
      // whatever the page behind the interstitial is showing (dashboard plans).
      await client.refetchQueries({ include: 'active' });
    } catch (_error) {
      setErrorMessages([
        {
          path: 'base',
          message: 'We could not save your acknowledgment. Please try again.',
        },
      ]);
    }
  }

  // Consent (AccountConsentGate) comes first, and only one gate is open at a
  // time: two Headless UI dialogs open together swallow each other's clicks.
  if (!item || exempt || needsConsent(account, currentUser)) {
    return null;
  }

  // Without a tier to show there is no price to disclose. Stand down rather
  // than lock the whole app behind a modal that can never be confirmed; the
  // order stays held server-side until the location's price book is fixed.
  if (Array.isArray(tiers) && tiers.length === 0) {
    console.warn(
      'Subscription disclosure skipped: no subscription tiers for this location.',
    );
    return null;
  }

  return (
    <>
      <SubscriptionDisclosureModal
        key={item.key}
        open={!isCardModalOpen}
        item={{
          id: item.orderItemId,
          product: item.product,
          patient: item.patient,
        }}
        onBack={noop}
        onConfirm={handleConfirm}
        isSubmitting={loading}
        errorMessages={errorMessages}
        dismissable={false}
        intro={`Your dental practice added this ${productNoun(item.product)} to your account. Before we can start making retainers, please review and confirm the subscription terms.`}
        position={{
          index: acknowledgedCount + 1,
          total: acknowledgedCount + items.length,
        }}
        tiers={tiers}
        positionScope="on your account"
        requiresCard={requiresCard}
        hasCard={hasCard}
        onAddCard={() => setIsCardModalOpen(true)}
        zdex={900}
      />
      {account?.id && (
        <AccountCreditCardModal
          open={isCardModalOpen}
          handleCloseModal={handleCardModalClose}
          accountId={account.id}
          title="Add a payment method"
        />
      )}
    </>
  );
}

export default SubscriptionAcknowledgmentGate;
